import { useEffect, useReducer } from "react";
import translate from "../../../i18n/translate";
import Loader from "../../common/elements/Loader";
import { WmButton } from "@watermarkinsights/ripple-react";
import { downloadPDF } from "../../common/helper";
import { sendRequest, sendRequestWithPayload } from "../../../common/http";
import { useIntl } from "react-intl";

interface ExportResultSet {
  pdfFileBytes: string;
}

interface TokenResultSet {
  token: string;
}
export interface HeaderProps {
  showLoader: boolean;
  title: string;
  planType?: number;
  showDownloadBtn: boolean;
}

interface HeaderAction {
  type: string;
  token: string | null;
  data: boolean;
}

const defaultHeaderAction = {
  type: "",
  token: "",
  data: false
};

interface HeaderState {
  token: string | null;
  isExportInProgress: boolean;
}

const reducer = (state: HeaderState, action: HeaderAction) => {
  switch (action.type) {
    case "setToken":
      return { ...state, token: action.token };
    case "setExportInProgress":
      return { ...state, isExportInProgress: action.data };
    default:
      return state;
  }
};

export default function Header({
  showLoader,
  title,
  planType,
  showDownloadBtn
}: HeaderProps): JSX.Element {
  const initialState = {
    token: null,
    isExportInProgress: false
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const subHeader = useIntl().formatMessage({
    id: "app.planParticipation.participationAssessment"
  });

  function exportToPdf() {
    let mainContent = document.getElementById("maincontent");
    let downloadText = document.querySelector(".download-text");
    if (downloadText) {
      downloadText.setAttribute("tabindex", "0");
      (downloadText as HTMLElement).focus();
    }

    if (mainContent) {
      let clone = mainContent.cloneNode(true);

      let headerSection = clone.firstChild;
      headerSection && clone.removeChild(headerSection);
      let wrapperDiv = document.createElement("div");
      wrapperDiv.appendChild(clone);

      let formData = new FormData();
      formData.append("html", wrapperDiv.outerHTML);
      formData.append("header", encodeURIComponent(subHeader));
      formData.append("sub_header", encodeURIComponent(title));
      formData.append("export_id", "plan-participation-export");
      formData.append("_csrf_token", state.token ? state.token : "");

      const applyResult = (result: ExportResultSet) => {
        const fileTitle =
          planType === 0
            ? "Participation_in_Program_Plans"
            : "Participation_in_Course_Plans";
        downloadPDF(result.pdfFileBytes, fileTitle);
        dispatch({ ...defaultHeaderAction, type: "setToken", token: null });
        dispatch({
          ...defaultHeaderAction,
          type: "setExportInProgress",
          data: false
        });
      };
      const onError = (error: string) => {
        // Log error in console for debugging
        console.log("API Error:", error);
        dispatch({ ...defaultHeaderAction, type: "setToken", token: null });
        dispatch({
          ...defaultHeaderAction,
          type: "setExportInProgress",
          data: false
        });
      };
      sendRequestWithPayload(
        {
          uri: "/export",
          body: formData,
          camelize: true
        },
        applyResult,
        onError
      );
    }
  }
  useEffect(() => {
    if (state.isExportInProgress) {
      sendRequest(
        {
          uri: "/export/token",
          camelize: true
        },
        (result: TokenResultSet) => {
          dispatch({
            ...defaultHeaderAction,
            type: "setToken",
            token: result.token
          });
        }
      );
    }
  }, [state.isExportInProgress]);
  useEffect(() => {
    if (state.token && state.isExportInProgress) {
      exportToPdf();
    }
  }, [state.isExportInProgress, state.token]);
  return (
    <>
      {showLoader && <Loader />}
      <section
        className="page-banner-container -mainheader -withbreadcrumbs only-breadcrumbs header-padding"
        id="page-header"
      >
        <div className="flex-row export-container">
          <div>
            <div>
              <span className="org-name uppercase">{subHeader}</span>
            </div>
            <div className="identity-details">
              <div className="page-header -titleonly">
                <h2 id="main-heading" className="plan-title-header">{title}</h2>
              </div>
            </div>
          </div>
          {showDownloadBtn && (
            <div className="flex-row export-button">
              {state.isExportInProgress && (
                <span className="download-text">
                  {translate(
                    "app.planParticipation.exportPdf.downloadReportText"
                  )}
                </span>
              )}
              <div className="button-container">
                <WmButton
                  button-type="secondary"
                  icon="f1da"
                  icon-size="20px"
                  onClick={() =>
                    dispatch({
                      ...defaultHeaderAction,
                      type: "setExportInProgress",
                      data: true
                    })
                  }
                  disabled={state.isExportInProgress}
                >
                  {translate("app.planParticipation.exportPdf.download")}
                </WmButton>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
